import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAuth } from '../contexts/AuthContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { campaignsAPI, templatesAPI, contactsAPI, contactListsAPI } from '../services/api.jsx';
import api from '../services/api.jsx';
import ConfirmModal from '../components/ConfirmModal.jsx';
import SuccessModal from '../components/SuccessModal.jsx';
import { normalizeName } from '../utils/nameNormalization';
import './CampaignSenderTypeahead.css';

const recipientEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CREATE_NEW_LIST_OPTION = '__CREATE_NEW_LIST__';
const extractEmailPattern = /[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

const splitNameParts = (fullName = '') => {
  const normalized = String(fullName || '').trim().replace(/\s+/g, ' ');
  if (!normalized) return { firstName: '', lastName: '' };

  if (normalized.includes(',')) {
    const [last, first] = normalized.split(',').map(part => String(part || '').trim());
    return {
      firstName: first || '',
      lastName: last || ''
    };
  }

  const parts = normalized.split(' ');
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' ')
  };
};

const cleanNameCandidate = (value = '') => {
  const domainOnlyPattern = /^[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i;

  const cleaned = String(value || '')
    .replace(/mailto:/gi, ' ')
    .replace(/https?:\/\/\S+/gi, ' ')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\bin\s+bing\b/gi, ' ')
    .replace(/^\s*[-–—•*]+\s*/u, '')
    .replace(/^\s*["(<\[]+/, '')
    .replace(/[">)\]]+\s*$/, '')
    .replace(/\s*[-–—]+\s*$/u, ' ')
    .replace(/^\s*(at|email|e-mail)\s+/i, '')
    .replace(/[^\p{L}\p{N}\s.'-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) return '';

  const filteredTokens = cleaned
    .split(' ')
    .map(token => token.trim())
    .filter(Boolean)
    .filter(token => !/^[-–—]+$/u.test(token))
    .filter(token => !domainOnlyPattern.test(token));

  const normalized = filteredTokens.join(' ').trim();
  if (!normalized || domainOnlyPattern.test(normalized)) return '';

  return normalized;
};

const cleanEmailCandidate = (value = '') => {
  return String(value || '')
    .replace(/^\s*mailto:/i, '')
    .replace(/[<>()\[\]{}"`,;]+/g, '')
    .replace(/^\.+|\.+$/g, '')
    .trim()
    .toLowerCase();
};

const parseManualRecipientEntries = (rawText = '') => {
  const text = String(rawText || '').replace(/\r/g, '\n');
  const entryChunks = text
    .split(/[\n,;|]+/)
    .map(chunk => chunk.trim())
    .filter(Boolean);

  const broadEmailCandidates = Array.from(new Set((text.match(extractEmailPattern) || [])
    .map(item => item.toLowerCase())));

  const byEmail = new Map();

  const registerEntry = ({ email, fullName = '' }) => {
    const rawEmail = String(email || '').trim();
    const normalizedEmail = cleanEmailCandidate(rawEmail);
    if (!normalizedEmail) return;

    const rawFullName = String(fullName || '').replace(/\s+/g, ' ').trim();
    const cleanedFullName = cleanNameCandidate(fullName);
    const normalizedFullName = normalizeName(cleanedFullName);
    const nameNeedsCleanup = Boolean(rawFullName && normalizedFullName !== rawFullName);
    const emailNeedsCleanup = Boolean(rawEmail && normalizedEmail !== rawEmail.toLowerCase());
    const needsCleanup = nameNeedsCleanup || emailNeedsCleanup;
    const { firstName, lastName } = splitNameParts(normalizedFullName);
    const existing = byEmail.get(normalizedEmail);

    if (!existing) {
      byEmail.set(normalizedEmail, {
        email: normalizedEmail,
        fullName: normalizedFullName,
        firstName,
        lastName,
        needsCleanup
      });
      return;
    }

    if (!existing.fullName && normalizedFullName) {
      byEmail.set(normalizedEmail, {
        ...existing,
        fullName: normalizedFullName,
        firstName: firstName || existing.firstName,
        lastName: lastName || existing.lastName,
        needsCleanup: existing.needsCleanup || needsCleanup
      });
      return;
    }

    if (needsCleanup && !existing.needsCleanup) {
      byEmail.set(normalizedEmail, {
        ...existing,
        needsCleanup: true
      });
    }
  };

  const pairPattern = /(?:^|[,;\n|])\s*([^,;\n<>]*?)\s*(?:mailto:)?([A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,})\b/gi;
  let pairMatch = null;
  while ((pairMatch = pairPattern.exec(text)) !== null) {
    const fullName = pairMatch[1] || '';
    const email = cleanEmailCandidate(pairMatch[2] || '');
    registerEntry({ email, fullName });
  }

  entryChunks.forEach((line) => {
    const lineEmails = line.match(extractEmailPattern) || [];
    if (!lineEmails.length) return;

    lineEmails.forEach((rawEmail) => {
      const email = cleanEmailCandidate(rawEmail);
      const lowerLine = line.toLowerCase();
      const emailIndex = lowerLine.indexOf(email);

      let nameCandidate = '';
      if (emailIndex >= 0) {
        const before = line.slice(0, emailIndex).trim();
        const after = line.slice(emailIndex + email.length).trim();

        if (before.endsWith('<')) {
          nameCandidate = before.slice(0, -1).trim();
        } else if (before && !before.includes('@')) {
          const beforeSegments = before.split(/[;|\-]+/).map(item => item.trim()).filter(Boolean);
          nameCandidate = beforeSegments[beforeSegments.length - 1] || '';
        } else if (after && !after.includes('@')) {
          const afterSegments = after.split(/[;|\-]+/).map(item => item.trim()).filter(Boolean);
          nameCandidate = afterSegments[0] || '';
        }
      }

      registerEntry({ email, fullName: nameCandidate });
    });
  });

  const entries = Array.from(byEmail.values());
  const validEntries = entries.filter(entry => recipientEmailPattern.test(entry.email));
  const invalidEmails = broadEmailCandidates.filter(email => !recipientEmailPattern.test(email));
  const needsCleanupCount = validEntries.filter(entry => entry.needsCleanup).length;

  const parsedCount = entryChunks.filter(chunk => /[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+/i.test(chunk)).length;

  return {
    parsedCount,
    uniqueCount: entries.length,
    validEntries,
    validEmails: validEntries.map(entry => entry.email),
    invalidEmails,
    needsCleanupCount
  };
};

const Campaigns = () => {
  const { user, hasAnyPermission } = useAuth();
  const canViewAllCampaigns = hasAnyPermission(['campaigns:read:any']);
  const [searchParams] = useSearchParams();
  const [campaigns, setCampaigns] = useState([]);
  const [showAllCampaigns, setShowAllCampaigns] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [lists, setLists] = useState([]);
  const [selectedLists, setSelectedLists] = useState([]);
  const [manualRecipientsCsv, setManualRecipientsCsv] = useState('');
  const [manualAssignListId, setManualAssignListId] = useState('');
  const [manualNewListName, setManualNewListName] = useState('');
  const [creatingManualList, setCreatingManualList] = useState(false);
  const [newlyCreatedListId, setNewlyCreatedListId] = useState(null);
  const [addingManualRecipients, setAddingManualRecipients] = useState(false);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [createCampaignError, setCreateCampaignError] = useState('');
  const [confirmAction, setConfirmAction] = useState({ show: false, type: null, id: null });
  const [successMessage, setSuccessMessage] = useState({ show: false, title: '', message: '' });
  const [newTemplate, setNewTemplate] = useState({ name: '', subject: '', body: '', isGlobal: false });
  const [prefillApplied, setPrefillApplied] = useState(false);
  const [senderSuggestions, setSenderSuggestions] = useState([]);
  const [showSenderSuggestions, setShowSenderSuggestions] = useState(false);
  const [searchingSenders, setSearchingSenders] = useState(false);
  const [senderSearchError, setSenderSearchError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    templateId: '',
    senderMailbox: '',
    contactIds: []
  });
  const senderSearchDebounceRef = useRef(null);
  const senderSearchRequestRef = useRef(0);
  const senderSearchContainerRef = useRef(null);

  const manualRecipientPreview = useMemo(() => {
    return parseManualRecipientEntries(manualRecipientsCsv);
  }, [manualRecipientsCsv]);

  const selectedRecipientContacts = useMemo(() => {
    const contactsById = new Map((contacts || []).map(contact => [contact.ContactId, contact]));
    const selected = (formData.contactIds || [])
      .map(contactId => contactsById.get(contactId))
      .filter(Boolean);

    return selected.sort((a, b) => {
      const aLast = String(a?.LastName || '').trim().toLowerCase();
      const bLast = String(b?.LastName || '').trim().toLowerCase();
      const lastComparison = aLast.localeCompare(bLast);
      if (lastComparison !== 0) return lastComparison;

      const aFirst = String(a?.FirstName || '').trim().toLowerCase();
      const bFirst = String(b?.FirstName || '').trim().toLowerCase();
      const firstComparison = aFirst.localeCompare(bFirst);
      if (firstComparison !== 0) return firstComparison;

      return String(a?.Email || '').trim().toLowerCase()
        .localeCompare(String(b?.Email || '').trim().toLowerCase());
    });
  }, [contacts, formData.contactIds]);

  const selectedIndividualCount = selectedRecipientContacts.length;

  useEffect(() => {
    loadData();
  }, [showAllCampaigns, canViewAllCampaigns]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!senderSearchContainerRef.current?.contains(event.target)) {
        setShowSenderSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    return () => {
      if (senderSearchDebounceRef.current) {
        clearTimeout(senderSearchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (prefillApplied || templates.length === 0) return;

    const isNewCampaign = searchParams.get('new') === '1';
    if (!isNewCampaign) return;

    const templateIdParam = searchParams.get('templateId');
    const eventTitle = searchParams.get('eventTitle');
    const hasTemplate = templateIdParam && templates.some(t => String(t.TemplateId) === String(templateIdParam));

    setShowForm(true);
    setFormData(prev => ({
      ...prev,
      name: prev.name || (eventTitle ? `${eventTitle} Invite Campaign` : prev.name),
      templateId: hasTemplate ? String(templateIdParam) : prev.templateId
    }));
    setPrefillApplied(true);
  }, [searchParams, templates, prefillApplied]);

  useEffect(() => {
    if (!showForm && createCampaignError) {
      setCreateCampaignError('');
    }
  }, [showForm, createCampaignError]);

  useEffect(() => {
    if (!showForm) return;
    if (String(formData.senderMailbox || '').trim()) return;

    const defaultSenderMailbox = String(
      user?.mail || user?.userPrincipalName || user?.email || ''
    ).trim().toLowerCase();

    if (!defaultSenderMailbox) return;

    setFormData(prev => {
      if (String(prev.senderMailbox || '').trim()) return prev;
      return {
        ...prev,
        senderMailbox: defaultSenderMailbox
      };
    });
  }, [showForm, user, formData.senderMailbox]);

  const loadData = async () => {
    try {
      setLoading(true);
      const campaignsRequest = canViewAllCampaigns && showAllCampaigns
        ? api.get('/admin/campaigns')
        : campaignsAPI.getAll();
      const [campaignsRes, templatesRes, contactsRes, listsRes] = await Promise.all([
        campaignsRequest,
        templatesAPI.getAll(),
        contactsAPI.getAll(),
        contactListsAPI.getAll()
      ]);
      setCampaigns(campaignsRes.data);
      setTemplates(templatesRes.data);
      setContacts(contactsRes.data);
      setLists(listsRes.data);
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setCreateCampaignError('');
      await campaignsAPI.create(formData);
      setShowForm(false);
      setFormData({ name: '', templateId: '', senderMailbox: '', contactIds: [] });
      setManualRecipientsCsv('');
      setManualAssignListId('');
      setSelectedLists([]);
      await loadData();
    } catch (err) {
      const message = err?.response?.data?.error || 'Failed to create campaign';
      setCreateCampaignError(message);
    }
  };

  const handleAddManualRecipients = async () => {
    if (!manualRecipientsCsv.trim()) {
      alert('Paste at least one recipient email address first.');
      return;
    }

    try {
      setAddingManualRecipients(true);
      let resolvedListId = manualAssignListId || null;

      if (manualAssignListId === CREATE_NEW_LIST_OPTION) {
        const trimmedListName = String(manualNewListName || '').trim();
        if (!trimmedListName) {
          alert('Enter a name for the new list.');
          return;
        }

        setCreatingManualList(true);
        const createdListResponse = await contactListsAPI.create({
          name: trimmedListName,
          description: 'Created from campaign manual recipients',
          isGlobal: false
        });

        const createdList = createdListResponse.data;
        resolvedListId = createdList?.ListId || null;

        if (!resolvedListId) {
          throw new Error('Failed to create list');
        }

        setLists(prev => [createdList, ...prev]);
        setManualAssignListId(resolvedListId);
        setManualNewListName('');
        setNewlyCreatedListId(resolvedListId);

        setTimeout(() => {
          setNewlyCreatedListId(current => (current === resolvedListId ? null : current));
        }, 3500);
      }

      const response = await campaignsAPI.addManualRecipients({
        recipientsCsv: manualRecipientsCsv,
        recipients: manualRecipientPreview.validEntries,
        listId: resolvedListId
      });

      const payload = response.data || {};
      const returnedContactIds = Array.isArray(payload.contactIds) ? payload.contactIds : [];

      setFormData(prev => ({
        ...prev,
        contactIds: Array.from(new Set([...(prev.contactIds || []), ...returnedContactIds]))
      }));

      if (resolvedListId) {
        setSelectedLists(prev => (prev.includes(resolvedListId) ? prev : [...prev, resolvedListId]));

        (async () => {
          try {
            const listDetailsResponse = await contactListsAPI.getOne(resolvedListId);
            const contactCount = Array.isArray(listDetailsResponse.data?.contacts)
              ? listDetailsResponse.data.contacts.length
              : 0;

            setLists(prev => prev.map(list => (
              list.ListId === resolvedListId
                ? { ...list, ContactCount: contactCount }
                : list
            )));
          } catch (refreshError) {
            console.warn('Failed to refresh list contact count asynchronously', refreshError);
          }
        })();
      }

      const refreshedContacts = await contactsAPI.getAll();
      setContacts(refreshedContacts.data || []);

      const invalidList = Array.isArray(payload.invalidEmails) && payload.invalidEmails.length
        ? ` Invalid emails skipped: ${payload.invalidEmails.join(', ')}`
        : '';

      setSuccessMessage({
        show: true,
        title: 'Recipients Added',
        message: `${payload.validCount || returnedContactIds.length} recipient(s) processed. ${payload.createdCount || 0} created, ${payload.existingCount || 0} already existed.${resolvedListId ? ` Assigned to list: ${payload.assignedToListCount || 0}.` : ''}${invalidList}`
      });

      setManualRecipientsCsv('');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add manual recipients');
    } finally {
      setCreatingManualList(false);
      setAddingManualRecipients(false);
    }
  };

  const handleParseManualRecipients = () => {
    const rawText = String(manualRecipientsCsv || '').trim();
    if (!rawText) {
      alert('Paste text with recipient emails first.');
      return;
    }

    const parsed = parseManualRecipientEntries(rawText);
    if (!parsed.validEntries.length) {
      alert('No email addresses were found in the pasted text.');
      return;
    }

    const normalizedText = parsed.validEntries
      .map(entry => (entry.fullName ? `${entry.fullName} <${entry.email}>` : entry.email))
      .join(', ');

    setManualRecipientsCsv(normalizedText);
    setSuccessMessage({
      show: true,
      title: 'Recipients Parsed',
      message: `Identified ${parsed.validEntries.length} unique email address(es). ${parsed.validEntries.filter(entry => entry.fullName).length} include a detected full name.`
    });
  };

  const handleSend = async () => {
    try {
      await campaignsAPI.send(confirmAction.id);
      setConfirmAction({ show: false, type: null, id: null });
      setSuccessMessage({ show: true, title: 'Success', message: 'Campaign queued for sending!' });
      await loadData();
    } catch (err) {
      alert('Failed to send campaign: ' + err.response?.data?.error);
      setConfirmAction({ show: false, type: null, id: null });
    }
  };

  const handleStop = async () => {
    try {
      await campaignsAPI.stop(confirmAction.id);
      setConfirmAction({ show: false, type: null, id: null });
      setSuccessMessage({ show: true, title: 'Success', message: 'Campaign stopped successfully!' });
      await loadData();
    } catch (err) {
      alert('Failed to stop campaign: ' + err.response?.data?.error);
      setConfirmAction({ show: false, type: null, id: null });
    }
  };

  const handleDelete = async () => {
    try {
      await campaignsAPI.delete(confirmAction.id);
      setConfirmAction({ show: false, type: null, id: null });
      setSuccessMessage({ show: true, title: 'Success', message: 'Campaign deleted successfully!' });
      await loadData();
    } catch (err) {
      alert('Failed to delete campaign: ' + err.response?.data?.error);
      setConfirmAction({ show: false, type: null, id: null });
    }
  };

  const handleSenderMailboxChange = (value) => {
    setFormData(prev => ({ ...prev, senderMailbox: value }));

    const term = String(value || '').trim();
    if (senderSearchDebounceRef.current) {
      clearTimeout(senderSearchDebounceRef.current);
    }

    if (term.length < 2) {
      setSenderSuggestions([]);
      setShowSenderSuggestions(false);
      setSearchingSenders(false);
      setSenderSearchError('');
      return;
    }

    senderSearchDebounceRef.current = setTimeout(async () => {
      const requestId = senderSearchRequestRef.current + 1;
      senderSearchRequestRef.current = requestId;

      try {
        setSearchingSenders(true);
        setSenderSearchError('');

        const response = await api.get('/events/entra-users/search', {
          params: { q: term }
        });

        if (senderSearchRequestRef.current !== requestId) {
          return;
        }

        const items = Array.isArray(response.data) ? response.data : [];
        setSenderSuggestions(items);
        setShowSenderSuggestions(true);
      } catch (err) {
        if (senderSearchRequestRef.current !== requestId) {
          return;
        }

        setSenderSuggestions([]);
        setShowSenderSuggestions(false);
        setSenderSearchError(err.response?.data?.error || 'Unable to search Entra users right now.');
      } finally {
        if (senderSearchRequestRef.current === requestId) {
          setSearchingSenders(false);
        }
      }
    }, 250);
  };

  const handleSelectSenderSuggestion = (selectedUser) => {
    const email = String(selectedUser?.mail || selectedUser?.userPrincipalName || '').trim();
    if (!email) {
      return;
    }

    setFormData(prev => ({ ...prev, senderMailbox: email }));
    setSenderSuggestions([]);
    setShowSenderSuggestions(false);
    setSearchingSenders(false);
    setSenderSearchError('');
  };

  const toggleContact = (contactId) => {
    setFormData(prev => ({
      ...prev,
      contactIds: prev.contactIds.includes(contactId)
        ? prev.contactIds.filter(id => id !== contactId)
        : [...prev.contactIds, contactId]
    }));
  };

  const selectAll = () => {
    setFormData(prev => ({
      ...prev,
      contactIds: contacts.map(c => c.ContactId)
    }));
  };

  const toggleList = async (listId) => {
    if (selectedLists.includes(listId)) {
      // Deselect the list
      setSelectedLists(prev => prev.filter(id => id !== listId));
      // Remove contacts from this list
      try {
        const response = await contactListsAPI.getOne(listId);
        const listContacts = response.data.contacts || [];
        const listContactIds = listContacts.map(c => c.ContactId);
        setFormData(prev => ({
          ...prev,
          contactIds: prev.contactIds.filter(id => !listContactIds.includes(id))
        }));
      } catch (err) {
        console.error('Failed to load list contacts', err);
      }
    } else {
      // Select the list
      setSelectedLists(prev => [...prev, listId]);
      // Add contacts from this list
      try {
        const response = await contactListsAPI.getOne(listId);
        const listContacts = response.data.contacts || [];
        const listContactIds = listContacts.map(c => c.ContactId);
        setFormData(prev => ({
          ...prev,
          contactIds: [...new Set([...prev.contactIds, ...listContactIds])]
        }));
      } catch (err) {
        console.error('Failed to load list contacts', err);
      }
    }
  };

  const selectAllLists = async () => {
    setSelectedLists(lists.map(l => l.ListId));
    // Collect all unique contact IDs from all lists
    const allContactIds = new Set(formData.contactIds);
    for (const list of lists) {
      try {
        const response = await contactListsAPI.getOne(list.ListId);
        const listContacts = response.data.contacts || [];
        listContacts.forEach(c => allContactIds.add(c.ContactId));
      } catch (err) {
        console.error('Failed to load list contacts', err);
      }
    }
    setFormData(prev => ({
      ...prev,
      contactIds: Array.from(allContactIds)
    }));
  };
  const handleTemplateChange = (e) => {
    const value = e.target.value;
    if (value === 'CREATE_NEW') {
      setShowTemplateForm(true);
      setFormData({ ...formData, templateId: '' });
    } else {
      setFormData({ ...formData, templateId: value });
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent parent form submission
    
    // Validate body has actual content (not just empty HTML tags)
    const strippedBody = newTemplate.body.replace(/<[^>]*>/g, '').trim();
    if (!strippedBody) {
      alert('Please enter email body content');
      return;
    }
    
    if (!newTemplate.name || !newTemplate.subject) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      setCreatingTemplate(true);
      console.log('Creating template:', newTemplate);
      const response = await templatesAPI.create(newTemplate);
      console.log('Template created:', response.data);
      await loadData();
      setFormData({ ...formData, templateId: response.data.TemplateId });
      setShowTemplateForm(false);
      setNewTemplate({ name: '', subject: '', body: '', isGlobal: false });
      setSuccessMessage({ show: true, title: 'Success', message: 'Template created successfully!' });
    } catch (err) {
      console.error('Template creation error:', err);
      alert('Failed to create template: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreatingTemplate(false);
    }
  };

  const handleCancelTemplateCreation = () => {
    setShowTemplateForm(false);
    setNewTemplate({ name: '', subject: '', body: '', isGlobal: false });
  };

  if (loading) return <div className="loading">Loading campaigns...</div>;

  return (
    <div className="container">
      {/* Template Creation Modal */}
      {showTemplateForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Create New Template</h3>
            <form onSubmit={handleCreateTemplate}>
              <input
                type="text"
                placeholder="Template Name *"
                required
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                style={{ marginBottom: '10px' }}
              />
              <input
                type="text"
                placeholder="Email Subject *"
                required
                value={newTemplate.subject}
                onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })}
                style={{ marginBottom: '10px' }}
              />
              <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <label style={{ fontWeight: 'bold' }}>Email Body *</label>
                  <button 
                    type="button" 
                    onClick={() => setShowHtmlSource(!showHtmlSource)}
                    className="btn"
                    style={{ padding: '5px 15px', fontSize: '12px', backgroundColor: '#6c757d', color: 'white' }}
                  >
                    {showHtmlSource ? '📝 Visual Editor' : '< > HTML Source'}
                  </button>
                </div>
                {showHtmlSource ? (
                  <textarea
                    value={newTemplate.body}
                    onChange={(e) => setNewTemplate({ ...newTemplate, body: e.target.value })}
                    rows="15"
                    style={{ fontFamily: 'monospace', fontSize: '13px' }}
                    placeholder="Enter HTML code here..."
                  />
                ) : (
                  <ReactQuill
                    theme="snow"
                    value={newTemplate.body}
                    onChange={(content) => setNewTemplate({ ...newTemplate, body: content })}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        [{ 'color': [] }, { 'background': [] }],
                        [{ 'align': [] }],
                        ['link', 'image'],
                        ['clean']
                      ]
                    }}
                    style={{ backgroundColor: 'white', minHeight: '200px' }}
                    placeholder="Type your email content here... Use {{FirstName}}, {{LastName}}, {{Email}}, {{Company}}, {{JobTitle}} for personalization"
                  />
                )}
              </div>
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '4px', fontSize: '14px', marginBottom: '15px' }}>
                <strong>Available Placeholders:</strong> <code>{'{{FirstName}}, {{LastName}}, {{Email}}, {{Company}}, {{JobTitle}}'}</code>
              </div>
              <div style={{ textAlign: 'left', marginBottom: '15px' }}>
                <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={newTemplate.isGlobal}
                    onChange={(e) => setNewTemplate({ ...newTemplate, isGlobal: e.target.checked })}
                  />
                  <span>Make this template global (visible to all users)</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" className="btn btn-primary" disabled={creatingTemplate}>
                  {creatingTemplate ? 'Creating...' : 'Create Template'}
                </button>
                <button type="button" onClick={handleCancelTemplateCreation} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Campaigns</h1>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {canViewAllCampaigns && (
            <button
              onClick={() => setShowAllCampaigns(prev => !prev)}
              className="btn btn-secondary"
            >
              {showAllCampaigns ? 'Show My Campaigns' : 'Show All Campaigns'}
            </button>
          )}
          <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
            {showForm ? 'Cancel' : 'New Campaign'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="card">
          <h2>Create Campaign</h2>
          {createCampaignError && (
            <div style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: '6px', background: '#fde7e9', color: '#a4262c', border: '1px solid #f3b6bd' }}>
              {createCampaignError}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="Campaign Name *"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            
            <select
              required
              value={formData.templateId}
              onChange={handleTemplateChange}
            >
              <option value="">Select Template *</option>
              {templates.map(t => (
                <option key={t.TemplateId} value={t.TemplateId}>{t.Name}</option>
              ))}
              <option value="CREATE_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ Create New Template</option>
            </select>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Send As Mailbox *</label>
              <div className="event-contact-typeahead" ref={senderSearchContainerRef}>
                <input
                  type="email"
                  placeholder="rep@rsc.com"
                  required
                  value={formData.senderMailbox}
                  onChange={(e) => handleSenderMailboxChange(e.target.value)}
                  onFocus={() => {
                    if (senderSuggestions.length > 0) {
                      setShowSenderSuggestions(true);
                    }
                  }}
                />
                {showSenderSuggestions && (
                  <div className="event-contact-suggestions" role="listbox">
                    {searchingSenders ? (
                      <div className="event-contact-suggestion muted">Searching users...</div>
                    ) : senderSuggestions.length === 0 ? (
                      <div className="event-contact-suggestion muted">No Entra users found</div>
                    ) : (
                      senderSuggestions.map(userItem => {
                        const email = String(userItem.mail || userItem.userPrincipalName || '').trim();
                        const name = String(userItem.displayName || '').trim() || email;
                        const key = `${email}-${userItem.id || name}`;
                        return (
                          <button
                            key={key}
                            type="button"
                            className="event-contact-suggestion"
                            onClick={() => handleSelectSenderSuggestion(userItem)}
                          >
                            <span className="name">{name}</span>
                            <span className="email">{email}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
              <small className="helper-text" style={{ marginTop: '6px', marginBottom: '0' }}>
                Search Entra users to pick a sender mailbox, or type one manually. This field auto-fills with your mailbox by default.
              </small>
              {senderSearchError && (
                <small className="helper-text" style={{ color: '#a4262c', marginTop: '4px', marginBottom: 0 }}>
                  {senderSearchError}
                </small>
              )}
            </div>

            <div style={{ margin: '20px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label><strong>Select Recipients ({formData.contactIds.length} selected)</strong></label>
              </div>

              <div style={{ marginBottom: '20px', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fafafa' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                  Manually Add Recipients
                </label>
                <textarea
                  value={manualRecipientsCsv}
                  onChange={(e) => setManualRecipientsCsv(e.target.value)}
                  rows={3}
                  placeholder="paste in email addresses, separated by commas, semicolons, new lines, or pipes. You can also include names like John Doe <john.doe@example.com>"
                  style={{ width: '100%', marginBottom: '8px' }}
                />
               
                {manualRecipientPreview.parsedCount > 0 && (
                  <div style={{ marginBottom: '8px', fontSize: '13px' }}>
                    <div>
                      Parsed: <strong>{manualRecipientPreview.parsedCount}</strong> • Unique: <strong>{manualRecipientPreview.uniqueCount}</strong> • Valid: <strong style={{ color: '#027a48' }}>{manualRecipientPreview.validEmails.length}</strong> • Invalid: <strong style={{ color: '#b42318' }}>{manualRecipientPreview.invalidEmails.length}</strong> • Needs cleanup: <strong style={{ color: manualRecipientPreview.needsCleanupCount > 0 ? '#b54708' : '#027a48' }}>{manualRecipientPreview.needsCleanupCount}</strong>
                    </div>
                    {manualRecipientPreview.invalidEmails.length > 0 && (
                      <div style={{ marginTop: '4px', color: '#b42318' }}>
                        Invalid emails: {manualRecipientPreview.invalidEmails.slice(0, 8).join(', ')}{manualRecipientPreview.invalidEmails.length > 8 ? '…' : ''}
                      </div>
                    )}
                    {manualRecipientPreview.needsCleanupCount > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleParseManualRecipients}
                          disabled={addingManualRecipients}
                        >
                          Parse
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#475467' }}>List Assignment</label>
                    <select
                      value={manualAssignListId}
                      onChange={(e) => setManualAssignListId(e.target.value)}
                      style={{ width: '190px' }}
                    >
                      <option value="">Do not assign to a list</option>
                      <option value={CREATE_NEW_LIST_OPTION}>+ Create New List</option>
                      {lists.map(list => (
                        <option key={list.ListId} value={list.ListId}>{list.Name}</option>
                      ))}
                    </select>
                    {manualAssignListId === CREATE_NEW_LIST_OPTION && (
                      <input
                        type="text"
                        placeholder="New list name"
                        value={manualNewListName}
                        onChange={(e) => setManualNewListName(e.target.value)}
                        style={{ width: '190px' }}
                      />
                    )}
                    {manualRecipientPreview.parsedCount > 0 && manualRecipientPreview.validEmails.length > 0 && manualRecipientPreview.needsCleanupCount === 0 && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAddManualRecipients}
                        disabled={addingManualRecipients}
                        style={{ alignSelf: 'flex-start' }}
                      >
                        {addingManualRecipients
                          ? (creatingManualList ? 'Creating List...' : 'Adding...')
                          : 'Add Recipients'}
                      </button>
                    )}
                  </div>
                </div>
                <small className="helper-text" style={{ marginTop: '6px', marginBottom: 0 }}>
                  Added recipients are saved as contacts, selected for this campaign, and optionally assigned to the chosen list.
                </small>
              </div>

              {/* Lists Selection */}
              {lists.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <label style={{ fontSize: '14px', fontWeight: 'bold' }}>Contact Lists ({selectedLists.length} selected)</label>
                    <button type="button" onClick={selectAllLists} className="btn btn-secondary" style={{ padding: '5px 15px', fontSize: '12px' }}>
                      Select All Lists
                    </button>
                  </div>
                  <div style={{ maxHeight: '150px', overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '10px', backgroundColor: '#f9f9f9' }}>
                    {lists.map(list => (
                      <div
                        key={list.ListId}
                        style={{
                          padding: '5px 0',
                          backgroundColor: newlyCreatedListId === list.ListId ? '#ecfdf3' : 'transparent',
                          border: newlyCreatedListId === list.ListId ? '1px solid #abefc6' : '1px solid transparent',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s ease, border-color 0.2s ease'
                        }}
                      >
                        <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedLists.includes(list.ListId)}
                            onChange={() => toggleList(list.ListId)}
                            style={{ marginRight: '10px', width: 'auto' }}
                          />
                          📋 {list.Name} ({list.ContactCount || 0} contacts)
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Individual Contacts Selection */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <label style={{ fontSize: '14px', fontWeight: 'bold' }}>
                    Individual Contacts ({selectedIndividualCount} selected)
                  </label>
                  <button type="button" onClick={selectAll} className="btn btn-secondary" style={{ padding: '5px 15px', fontSize: '12px' }}>
                    Select All Contacts
                  </button>
                </div>
                {/* Contact Search */}
                <div style={{ marginBottom: '10px' }}>
                  <input
                    type="text"
                    placeholder="Search contacts by name or email..."
                    value={contactSearchQuery}
                    onChange={(e) => setContactSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '13px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
                <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '10px' }}>
                  {contacts
                    .filter(contact => {
                      if (!contactSearchQuery.trim()) return true;
                      const query = contactSearchQuery.toLowerCase();
                      return (
                        (contact.FirstName && contact.FirstName.toLowerCase().includes(query)) ||
                        (contact.LastName && contact.LastName.toLowerCase().includes(query)) ||
                        (contact.Email && contact.Email.toLowerCase().includes(query)) ||
                        (contact.Company && contact.Company.toLowerCase().includes(query)) ||
                        (contact.JobTitle && contact.JobTitle.toLowerCase().includes(query))
                      );
                    })
                    .map(contact => (
                    <div key={contact.ContactId} style={{ padding: '5px 0' }}>
                      <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={formData.contactIds.includes(contact.ContactId)}
                          onChange={() => toggleContact(contact.ContactId)}
                          style={{ marginRight: '10px', width: 'auto' }}
                        />
                        {contact.FirstName} {contact.LastName} ({contact.Email})
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '16px', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fafafa' }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                Recipients ({selectedRecipientContacts.length})
              </div>
              {selectedRecipientContacts.length === 0 ? (
                <div style={{ fontSize: '13px', color: '#666' }}>No recipients selected yet.</div>
              ) : (
                <div style={{ maxHeight: '140px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '8px', backgroundColor: '#fff' }}>
                  {selectedRecipientContacts.map(contact => {
                    const fullName = `${contact.FirstName || ''} ${contact.LastName || ''}`.trim();
                    return (
                      <div key={contact.ContactId} style={{ fontSize: '13px', padding: '2px 0' }}>
                        {fullName ? `${fullName} (${contact.Email})` : contact.Email}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary">Create Campaign</button>
          </form>
        </div>
      )}

      <div className="card">
        <h2>Campaign List ({campaigns.length})</h2>
        {campaigns.length === 0 ? (
          <p>No campaigns found. Create your first campaign to get started.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                {showAllCampaigns && canViewAllCampaigns && <th>Owner</th>}
                <th>Template</th>
                <th>Status</th>
                <th>Sent</th>
                <th>Success</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => (
                <tr key={campaign.CampaignId}>
                  <td><strong>{campaign.Name}</strong></td>
                  {showAllCampaigns && canViewAllCampaigns && (
                    <td>{campaign.OwnerDisplayName || campaign.OwnerEmail || campaign.UserId}</td>
                  )}
                  <td>{campaign.TemplateName}</td>
                  <td>
                    <span style={{ 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      backgroundColor: campaign.Status === 'sent' ? '#d4edda' : '#fff3cd'
                    }}>
                      {campaign.Status}
                    </span>
                  </td>
                  <td>{campaign.TotalSent || 0}</td>
                  <td>{campaign.SuccessCount || 0}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <Link 
                        to={`/campaigns/${campaign.CampaignId}`} 
                        title="View campaign details"
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer', 
                          fontSize: '18px',
                          padding: '4px',
                          textDecoration: 'none'
                        }}
                      >
                        👁️
                      </Link>
                      {campaign.Status === 'draft' && (
                        <button 
                          onClick={() => setConfirmAction({ show: true, type: 'send', id: campaign.CampaignId })} 
                          title="Send campaign"
                          style={{ 
                            background: 'none', 
                            border: 'none', 
                            cursor: 'pointer', 
                            fontSize: '18px',
                            padding: '4px'
                          }}
                        >
                          📤
                        </button>
                      )}
                      {campaign.Status === 'sending' && (
                        <button 
                          onClick={() => setConfirmAction({ show: true, type: 'stop', id: campaign.CampaignId })} 
                          title="Stop campaign"
                          style={{ 
                            background: 'none', 
                            border: 'none', 
                            cursor: 'pointer', 
                            fontSize: '18px',
                            padding: '4px'
                          }}
                        >
                          ⏸️
                        </button>
                      )}
                      <button 
                        onClick={() => setConfirmAction({ show: true, type: 'delete', id: campaign.CampaignId })} 
                        title="Delete campaign"
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer', 
                          fontSize: '18px',
                          padding: '4px'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmModal
        show={confirmAction.show}
        title={
          confirmAction.type === 'send' ? 'Send Campaign' :
          confirmAction.type === 'stop' ? 'Stop Campaign' :
          confirmAction.type === 'delete' ? 'Delete Campaign' : ''
        }
        message={
          confirmAction.type === 'send' ? 'Are you sure you want to send this campaign?' :
          confirmAction.type === 'stop' ? 'Are you sure you want to stop this campaign?' :
          confirmAction.type === 'delete' ? 'Are you sure you want to delete this campaign? This action cannot be undone.' : ''
        }
        onConfirm={
          confirmAction.type === 'send' ? handleSend :
          confirmAction.type === 'stop' ? handleStop :
          confirmAction.type === 'delete' ? handleDelete : null
        }
        onCancel={() => setConfirmAction({ show: false, type: null, id: null })}
        confirmText={confirmAction.type === 'delete' ? 'Delete' : 'Confirm'}
        confirmStyle={confirmAction.type === 'delete' ? 'danger' : 'primary'}
      />

      <SuccessModal
        show={successMessage.show}
        title={successMessage.title}
        message={successMessage.message}
        onClose={() => setSuccessMessage({ show: false, title: '', message: '' })}
      />
    </div>
  );
};

const CampaignsWithProtection = () => (
  <ProtectedRoute requiredTab="campaigns">
    <Campaigns />
  </ProtectedRoute>
);

export default CampaignsWithProtection;
